import { assertNever } from "@redotech/util/type";
import type Stripe from "stripe";
import * as z from "zod";

export const paymentMethodStatusValue = z.enum([
  "active",
  "missing",
  "error",
  "requires_verification",
  "requires_reconnect",
]);

export type PaymentMethodStatus = z.infer<typeof paymentMethodStatusValue>;

/**
 * Whether a stored micro-deposit prompt should be dropped. `error` is transient,
 * so it holds the secret rather than stranding the merchant with no way to verify.
 */
export function verificationIsOver(status: PaymentMethodStatus): boolean {
  return status !== "requires_verification" && status !== "error";
}

/**
 * Stripe refuses to charge a PaymentMethod that isn't attached to the Customer,
 * so attachment — not SetupIntent status alone — decides "chargeable".
 */
export function classifyPaymentMethodAttachment({
  attachedCustomerId,
  setupIntentStatus,
}: {
  attachedCustomerId: string | null;
  setupIntentStatus: Stripe.SetupIntent.Status | null;
}): PaymentMethodStatus {
  if (attachedCustomerId) {
    return "active";
  }
  if (!setupIntentStatus) {
    return "requires_reconnect";
  }

  switch (setupIntentStatus) {
    case "requires_action":
    case "processing":
      return "requires_verification";
    /** Terminal with no attachment — only re-adding the method fixes it. */
    case "canceled":
    case "requires_confirmation":
    case "requires_payment_method":
    case "succeeded":
      return "requires_reconnect";
    default:
      return assertNever(setupIntentStatus);
  }
}

/** Stripe returns either the id or the expanded Customer (or a deleted stub). */
export function stripeCustomerId(
  customer: Stripe.PaymentMethod["customer"] | Stripe.SetupIntent["customer"],
): string | null {
  if (!customer) {
    return null;
  }
  return typeof customer === "string" ? customer : customer.id;
}
